import { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Fade,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  BASE_UNIT_LABELS,
  INGREDIENT_SECTORS,
  formatYieldInput,
  parseYieldPercentage,
} from "../../stock/baseDatos/insumos/utils/ingredientFormatters";

const EMPTY_FORM = {
  name: "",
  category: "",
  sector: "",
  baseUnit: "",
  yieldPercentage: "100",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    bgcolor: "common.white",
    fontSize: 14,
    fontWeight: 650,
    "& fieldset": { borderColor: "neutral.150" },
    "&:hover fieldset": { borderColor: "neutral.300" },
    "&.Mui-focused fieldset": { borderColor: "primary.500" },
  },
};

const actionButtonSx = {
  minWidth: 120,
  borderRadius: "24px",
  textTransform: "none",
  fontWeight: 800,
  boxShadow: "none",
};

function initialForm(item) {
  if (!item) return EMPTY_FORM;
  return {
    name: item.name || "",
    category: item.category || "",
    sector: Array.isArray(item.sector) ? item.sector[0] || "" : item.sector || "",
    baseUnit: item.baseUnit || "",
    yieldPercentage: formatYieldInput(item.defaultYieldFactor),
  };
}

export default function GlobalIngredientFormDialog({
  open,
  item,
  categories = [],
  saving,
  apiError,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initialForm(item));
      setSubmitted(false);
    }
  }, [open, item]);

  const errors = useMemo(() => {
    const result = {};
    const name = form.name.trim();
    const category = form.category.trim();
    const parsedYield = parseYieldPercentage(form.yieldPercentage);
    if (!name) result.name = "Ingresá el nombre.";
    else if (name.length > 200) result.name = "El nombre admite hasta 200 caracteres.";
    if (!category) result.category = "Ingresá la categoría.";
    else if (category.length > 120) result.category = "La categoría admite hasta 120 caracteres.";
    if (!INGREDIENT_SECTORS.includes(form.sector)) result.sector = "Seleccioná un sector.";
    if (!Object.hasOwn(BASE_UNIT_LABELS, form.baseUnit)) result.baseUnit = "Seleccioná una unidad base.";
    if (parsedYield === null || parsedYield === undefined) {
      result.yieldPercentage = "Ingresá un porcentaje entre 0 y 100, con hasta 2 decimales.";
    }
    return result;
  }, [form]);

  const update = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const submit = (event) => {
    event.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length) return;
    onSubmit({
      name: form.name.trim(),
      category: form.category.trim(),
      sector: form.sector,
      baseUnit: form.baseUnit,
      defaultYieldFactor: parseYieldPercentage(form.yieldPercentage),
    });
  };

  const handleClose = () => {
    if (!saving) onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      PaperProps={{
        sx: {
          bgcolor: "transparent",
          boxShadow: "none",
          borderRadius: "30px",
          overflow: "visible",
          m: 1.5,
        },
      }}
      slotProps={{
        backdrop: {
          timeout: 180,
          sx: { bgcolor: "rgba(2, 8, 23, 0.45)", backdropFilter: "blur(3px)" },
        },
      }}
      aria-labelledby="global-ingredient-form-title"
    >
      <Fade in={open} timeout={180}>
        <Box
          component="form"
          id="global-ingredient-form"
          onSubmit={submit}
          noValidate
          sx={{
            width: "min(92vw, 720px)",
            maxHeight: "92dvh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            bgcolor: "neutral.300",
            borderRadius: "30px",
            p: { xs: 2, md: 2.5 },
            boxShadow: "0px 20px 60px rgba(0,0,0,.28)",
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ flexShrink: 0 }}>
            <Box>
              <Typography id="global-ingredient-form-title" sx={{ fontSize: 20, fontWeight: 850, color: "primary.900" }}>
                {item ? "Editar insumo global" : "Crear insumo global"}
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: 12.5, fontWeight: 600, color: "neutral.700" }}>
                Administrá la información compartida por todas las licencias.
              </Typography>
            </Box>
            <IconButton onClick={handleClose} disabled={saving} aria-label="Cerrar modal">
              <CloseIcon sx={{ color: "primary.900" }} />
            </IconButton>
          </Stack>

          <Box sx={{ mt: 1.8, minHeight: 0, overflowY: "auto", borderRadius: "20px", bgcolor: "common.white", p: { xs: 2, md: 2.5 } }}>
            <Stack spacing={2}>
              <Alert severity="info" sx={{ borderRadius: "14px", fontWeight: 650 }}>
                Las ediciones se reflejarán en todas las licencias que tengan vinculado este insumo.
              </Alert>
              {apiError ? <Alert severity="error" sx={{ borderRadius: "14px" }}>{apiError}</Alert> : null}

              <TextField sx={fieldSx} label="Nombre" value={form.name} onChange={update("name")} error={submitted && Boolean(errors.name)} helperText={submitted && errors.name} inputProps={{ maxLength: 200 }} required autoFocus />
              <TextField sx={fieldSx} label="Categoría global" value={form.category} onChange={update("category")} error={submitted && Boolean(errors.category)} helperText={submitted && errors.category} inputProps={{ maxLength: 120, list: "global-ingredient-categories" }} required />
              <datalist id="global-ingredient-categories">
                {categories.map((value) => <option value={value} key={value} />)}
              </datalist>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField sx={fieldSx} select label="Sector" inputProps={{ "aria-label": "Sector" }} value={form.sector} onChange={update("sector")} error={submitted && Boolean(errors.sector)} helperText={submitted && errors.sector} required fullWidth>
                  {INGREDIENT_SECTORS.map((value) => <MenuItem value={value} key={value}>{value}</MenuItem>)}
                </TextField>
                <TextField sx={fieldSx} select label="Unidad base" inputProps={{ "aria-label": "Unidad base" }} value={form.baseUnit} onChange={update("baseUnit")} error={submitted && Boolean(errors.baseUnit)} helperText={submitted && errors.baseUnit} required fullWidth>
                  {Object.entries(BASE_UNIT_LABELS).map(([value, label]) => <MenuItem value={value} key={value}>{label}</MenuItem>)}
                </TextField>
              </Stack>

              <TextField
                sx={fieldSx}
                label="Rendimiento por defecto"
                value={form.yieldPercentage}
                onChange={update("yieldPercentage")}
                error={submitted && Boolean(errors.yieldPercentage)}
                helperText={(submitted && errors.yieldPercentage) || "Porcentaje entre 0 y 100."}
                required
                InputProps={{ endAdornment: <Typography color="text.secondary">%</Typography> }}
                inputProps={{ inputMode: "decimal" }}
              />
            </Stack>
          </Box>

          <Stack direction="row" justifyContent="flex-end" spacing={1.2} sx={{ pt: 1.8, flexShrink: 0 }}>
            <Button
              onClick={handleClose}
              disabled={saving}
              variant="contained"
              sx={{ ...actionButtonSx, bgcolor: "neutral.100", color: "primary.900", "&:hover": { bgcolor: "neutral.150", boxShadow: "none" } }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              sx={{
                minWidth: 145,
                height: 40,
                borderRadius: "33px",
                bgcolor: "primary.500",
                color: "logoblanco",
                textTransform: "none",
                fontWeight: 800,
                "&:hover": { bgcolor: "primary.600" },
                "&.Mui-disabled": { bgcolor: "neutral.500", color: "neutral.700" },
              }}
            >
              {saving ? (
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <CircularProgress size={14} sx={{ color: "logoblanco" }} />
                  <span>Guardando…</span>
                </Stack>
              ) : (
                "Guardar"
              )}
            </Button>
          </Stack>
        </Box>
      </Fade>
    </Dialog>
  );
}
